import { useState } from 'react'
import { CONDITIONS_DATA } from '@shared/rules/data/conditions'
import { cn } from '@/lib/cn'

/** The table every physical DM screen prints on the inside. */
const DC_GUIDE = [
  ['Very easy', 5], ['Easy', 10], ['Medium', 15],
  ['Hard', 20], ['Very hard', 25], ['Nearly impossible', 30],
] as const

const COMBAT_ACTIONS = [
  ['Dash', 'Extra movement equal to your speed'],
  ['Disengage', 'Your movement provokes no opportunity attacks'],
  ['Dodge', 'Attacks against you have disadvantage; Dex saves have advantage'],
  ['Help', 'Give an ally advantage on one check or attack'],
  ['Hide', 'Make a Stealth check to become unseen'],
  ['Ready', 'Prepare an action with a trigger'],
  ['Search', 'Make a Perception or Investigation check'],
  ['Shove', 'Athletics vs Athletics/Acrobatics — push 5 ft or knock prone'],
  ['Grapple', 'Unarmed strike; target makes a save or is Grappled'],
  ['Study', 'Make an Arcana, History, Nature, or Religion check'],
] as const

type Panel = 'conditions' | 'dcs' | 'actions'

export function QuickReference() {
  const [panel, setPanel] = useState<Panel>('conditions')
  const [open, setOpen] = useState<string | null>(null)

  return (
    <div>
      <div className="mb-3 flex gap-1 rounded-xl bg-ink-900/60 p-1">
        {([['conditions', 'Conditions'], ['dcs', 'DCs'], ['actions', 'Actions']] as const).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setPanel(id)}
            className={cn('pressable flex-1 rounded-lg px-2 py-1.5 text-[12px] font-medium',
              panel === id ? 'bg-ink-750 text-ink-100' : 'text-ink-400 hover:text-ink-200')}>
            {label}
          </button>
        ))}
      </div>

      {panel === 'conditions' && (
        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {CONDITIONS_DATA.map((condition) => (
            <div key={condition.id}>
              <button type="button" onClick={() => setOpen(open === condition.id ? null : condition.id)}
                className="flex w-full items-baseline justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-fill">
                <span className="text-[13px] text-ink-200">{condition.name}</span>
                <span className="truncate text-[11px] text-ink-500">{condition.summary}</span>
              </button>
              {open === condition.id && (
                <ul className="space-y-1 px-2 pb-2 pl-4 text-[12px] leading-relaxed text-ink-400">
                  {condition.effects.map((effect) => <li key={effect}>• {effect}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {panel === 'dcs' && (
        <ul className="space-y-1">
          {DC_GUIDE.map(([label, dc]) => (
            <li key={label} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-[13px]">
              <span className="text-ink-300">{label}</span>
              <span className="font-mono text-gold-400">{dc}</span>
            </li>
          ))}
        </ul>
      )}

      {panel === 'actions' && (
        <ul className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {COMBAT_ACTIONS.map(([name, what]) => (
            <li key={name} className="rounded-lg px-2 py-1.5">
              <p className="text-[13px] text-ink-200">{name}</p>
              <p className="text-[11px] leading-snug text-ink-500">{what}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
